/**
 * `<PasskeyLivesAnimation>` — short SVG/CSS animation showing
 * the passkey moving from "in the browser" into the user's
 * chosen authenticator. Per the design-system contract §4.2.
 *
 * Communicates the mental model: *"your passkey is in your
 * control, on your device or in your password manager — not on
 * our server."* Plays once on mount and rests at the
 * end-state.
 *
 * # Description (for designers / AI tools)
 *
 * A small horizontal scene, ~280×120px. On the left, a browser
 * window outline with a key glyph inside. On the right, an
 * authenticator's "destination" — a phone (for device
 * authenticators), a small generic shape labelled with the
 * authenticator family name (for password managers), or a
 * USB-key shape (for hardware keys). Between them, a single
 * key glyph animates from the browser into the destination
 * over ~1.5 seconds: scale-up briefly, translate across, drop
 * into the destination with a gentle bounce, then a soft
 * green checkmark appears beside the destination.
 *
 * Reduced-motion users see the end-state directly: key already
 * in destination, checkmark visible.
 *
 * Tokens: foreground (line work), primary (the key glyph in
 * flight), success (checkmark), muted-foreground (subtle
 * detail).
 */

import type { ReactElement } from "react";

export type AuthenticatorFamily =
  | "apple-keychain"
  | "google-password-manager"
  | "windows-hello"
  | "1password"
  | "bitwarden"
  | "yubikey"
  | "generic-platform"
  | "generic-roaming";

export interface PasskeyLivesAnimationProps {
  authenticator: AuthenticatorFamily;
  className?: string;
  width?: number;
}

interface DestinationDef {
  label: string;
  renderShape: () => ReactElement;
}

const DESTINATIONS: Record<AuthenticatorFamily, DestinationDef> = {
  "apple-keychain": {
    label: "iCloud Keychain",
    renderShape: () => <DeviceShape kind="apple" />,
  },
  "google-password-manager": {
    label: "Google Password Manager",
    renderShape: () => <DeviceShape kind="generic-device" />,
  },
  "windows-hello": {
    label: "Windows Hello",
    renderShape: () => <DeviceShape kind="windows" />,
  },
  "1password": {
    label: "1Password",
    renderShape: () => <VaultShape />,
  },
  bitwarden: {
    label: "Bitwarden",
    renderShape: () => <VaultShape />,
  },
  yubikey: {
    label: "YubiKey",
    renderShape: () => <KeyShape />,
  },
  "generic-platform": {
    label: "Your device",
    renderShape: () => <DeviceShape kind="generic-device" />,
  },
  "generic-roaming": {
    label: "Your hardware key",
    renderShape: () => <KeyShape />,
  },
};

export function PasskeyLivesAnimation({
  authenticator,
  className,
  width = 280,
}: PasskeyLivesAnimationProps): ReactElement {
  const dest = DESTINATIONS[authenticator];
  return (
    <figure
      className={className}
      style={{ width, margin: 0 }}
      aria-label={`Your passkey will be saved to ${dest.label}.`}
    >
      <svg
        role="img"
        aria-labelledby={`pl-${authenticator}-title`}
        viewBox="0 0 280 120"
        width="100%"
        height="auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={`pl-${authenticator}-title`}>
          Your passkey will be saved to {dest.label}.
        </title>
        <style>
          {`
            .pl-key-flying {
              opacity: 0;
              animation: pl-fly 1500ms cubic-bezier(0.4, 0, 0.2, 1) 200ms forwards;
              transform-origin: 60px 60px;
            }
            .pl-check {
              opacity: 0;
              animation: pl-checkmark 400ms ease-out 1600ms forwards;
              transform-origin: 240px 30px;
            }
            @keyframes pl-fly {
              0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
              15%  { opacity: 1; transform: translate(0, 0) scale(1.1); }
              80%  { opacity: 1; transform: translate(150px, 0) scale(1); }
              95%  { opacity: 1; transform: translate(155px, 6px) scale(0.9); }
              100% { opacity: 0; transform: translate(155px, 0) scale(0.6); }
            }
            @keyframes pl-checkmark {
              0%   { opacity: 0; transform: scale(0.5); }
              100% { opacity: 1; transform: scale(1); }
            }
            @media (prefers-reduced-motion: reduce) {
              .pl-key-flying { animation: none; opacity: 0; }
              .pl-check { animation: none; opacity: 1; }
            }
          `}
        </style>

        {/* Browser window on the left */}
        <g>
          <rect
            x="14"
            y="20"
            width="92"
            height="80"
            rx="6"
            fill="hsl(var(--card))"
            stroke="hsl(var(--foreground))"
            strokeWidth="1.4"
          />
          {/* Browser chrome */}
          <line x1="14" y1="34" x2="106" y2="34" stroke="hsl(var(--border))" strokeWidth="1" />
          <circle cx="22" cy="27" r="2" fill="hsl(var(--muted-foreground) / 0.5)" />
          <circle cx="30" cy="27" r="2" fill="hsl(var(--muted-foreground) / 0.5)" />
          <circle cx="38" cy="27" r="2" fill="hsl(var(--muted-foreground) / 0.5)" />
          <rect x="46" y="24" width="54" height="6" rx="2" fill="hsl(var(--muted) / 0.5)" />
          {/* Label */}
          <text
            x={60}
            y={108}
            fontSize="9"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight="500"
            fill="hsl(var(--muted-foreground))"
            textAnchor="middle"
          >
            this page
          </text>
        </g>

        {/* Static key at rest in the browser (visible until animation starts) */}
        <g transform="translate(46, 50)">
          <circle cx="6" cy="10" r="6" fill="none" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1.5" />
          <line x1="12" y1="10" x2="32" y2="10" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1.5" />
          <line x1="26" y1="10" x2="26" y2="13" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1.5" />
        </g>

        {/* Flying key — animated across */}
        <g className="pl-key-flying">
          <circle cx="60" cy="60" r="7" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.8" />
          <circle cx="60" cy="60" r="2.5" fill="hsl(var(--primary))" stroke="none" />
          <line x1="67" y1="60" x2="86" y2="60" stroke="hsl(var(--primary))" strokeWidth="1.8" />
          <line x1="80" y1="60" x2="80" y2="64" stroke="hsl(var(--primary))" strokeWidth="1.8" />
        </g>

        {/* Dotted trajectory line */}
        <path
          d="M 102 60 Q 160 38, 218 60"
          stroke="hsl(var(--muted-foreground) / 0.35)"
          strokeWidth="1.2"
          strokeDasharray="3 4"
          fill="none"
        />

        {/* Destination on the right */}
        <g transform="translate(184, 0)">{dest.renderShape()}</g>

        {/* Success check next to destination */}
        <g className="pl-check">
          <circle cx="240" cy="30" r="10" fill="hsl(var(--success))" />
          <path
            d="M 235 30 L 238 33 L 245 26"
            stroke="hsl(var(--success-foreground, 0 0% 100%))"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>

        {/* Destination label */}
        <text
          x={224}
          y={108}
          fontSize="9"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="600"
          fill="hsl(var(--foreground))"
          textAnchor="middle"
        >
          {dest.label}
        </text>
      </svg>
    </figure>
  );
}

/** Device-shape destination (phone or laptop frame). */
function DeviceShape({
  kind,
}: {
  kind: "apple" | "windows" | "generic-device";
}) {
  return (
    <g>
      {/* Generic device frame */}
      <rect
        x="14"
        y="34"
        width="72"
        height="52"
        rx={kind === "apple" ? "8" : "4"}
        fill="hsl(var(--card))"
        stroke="hsl(var(--foreground))"
        strokeWidth="1.4"
      />
      <rect
        x="20"
        y="40"
        width="60"
        height="40"
        rx="2"
        fill="hsl(var(--background))"
        stroke="hsl(var(--border) / 0.5)"
        strokeWidth="0.5"
      />
      {/* Key glyph inside the device, at rest */}
      <g transform="translate(36, 56)">
        <circle cx="6" cy="6" r="5" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
        <circle cx="6" cy="6" r="1.6" fill="hsl(var(--primary))" />
        <line x1="11" y1="6" x2="26" y2="6" stroke="hsl(var(--primary))" strokeWidth="1.5" />
        <line x1="22" y1="6" x2="22" y2="9" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      </g>
    </g>
  );
}

/** Vault-shape destination (password manager). */
function VaultShape() {
  return (
    <g>
      {/* Vault body */}
      <rect
        x="14"
        y="30"
        width="72"
        height="62"
        rx="6"
        fill="hsl(var(--card))"
        stroke="hsl(var(--foreground))"
        strokeWidth="1.5"
      />
      {/* Vault dial */}
      <circle cx="50" cy="60" r="14" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.8" />
      <circle cx="50" cy="60" r="3" fill="hsl(var(--primary))" />
      {/* Ticks */}
      {[0, 90, 180, 270].map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = 50 + Math.cos(rad) * 16;
        const y1 = 60 + Math.sin(rad) * 16;
        const x2 = 50 + Math.cos(rad) * 19;
        const y2 = 60 + Math.sin(rad) * 19;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
          />
        );
      })}
      {/* Lock shackle at top */}
      <path
        d="M 42 38 L 42 32 Q 42 26, 50 26 Q 58 26, 58 32 L 58 38"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
      />
    </g>
  );
}

/** Hardware-key destination (YubiKey-style USB). */
function KeyShape() {
  return (
    <g>
      {/* USB-key body */}
      <rect
        x="14"
        y="48"
        width="68"
        height="24"
        rx="4"
        fill="hsl(var(--card))"
        stroke="hsl(var(--foreground))"
        strokeWidth="1.5"
      />
      {/* USB connector */}
      <rect
        x="68"
        y="54"
        width="20"
        height="12"
        rx="2"
        fill="hsl(var(--muted) / 0.6)"
        stroke="hsl(var(--foreground))"
        strokeWidth="1.5"
      />
      {/* Touch sensor — gold disc */}
      <circle
        cx="34"
        cy="60"
        r="7"
        fill="hsl(var(--primary))"
        stroke="hsl(var(--foreground))"
        strokeWidth="1.2"
      />
      {/* Subtle keyhole notch */}
      <line x1="34" y1="57" x2="34" y2="63" stroke="hsl(var(--card))" strokeWidth="1.2" strokeLinecap="round" />

      {/* Indicator LED */}
      <circle cx="56" cy="60" r="1.5" fill="hsl(var(--success))" />
    </g>
  );
}
